import React, { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { JsonView, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';

import { Button, Input, Label, Card, CardHeader, CardBody, Icon } from '@repo/ui';
import { showToast } from '@repo/ui';
import { clioIntegrationApi, type JuniperDataObject } from '@xtnd/web/features/settings/api/clioIntegrationApi';
import { juniperQueryApi, type Transaction } from '../api';
import { authApi } from '@xtnd/web/features/auth/api';
interface FormData {
  operationalBillId: string;
  trustRequestId: string;
  juniperId?: string;
}

interface FormErrors {
  operationalBillId?: string;
  trustRequestId?: string;
  juniperId?: string;
  general?: string;
}

export function JuniperDataCard() {
  const [formData, setFormData] = useState<FormData>({
    operationalBillId: '',
    trustRequestId: '',
    juniperId: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [testResults, setTestResults] = useState<JuniperDataObject | null>(null);
  const [lookupInfo, setLookupInfo] = useState<{
    juniperId?: string;
    operationalInvoiceId?: string;
    trustRequestId?: string;
    clioPaymentLinkId?: string;
    clioPaymentLinkUrl?: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Get current user's tenant ID
  const currentUser = authApi.getCurrentUser();
  const tenantId = currentUser?.tenantId;

  async function lookupExistingTransaction(
    data: FormData
  ): Promise<{ tx: Transaction | null; resolvedOperationalId?: string; resolvedTrustId?: string }> {
    try {
      // Priority: juniperId > operationalBillId > trustRequestId
      if ((data.juniperId || '').trim()) {
        const tx = await juniperQueryApi.getTransaction(data.juniperId!.trim());
        if (tx) {
          setLookupInfo({
            juniperId: tx.juniperId,
            operationalInvoiceId: tx.operationalBill?.id
              ? String(tx.operationalBill.id)
              : undefined,
            trustRequestId: tx.trustRequestId,
            clioPaymentLinkId: tx.clioPaymentLinkId,
            clioPaymentLinkUrl: tx.clioPaymentLinkUrl,
          });
          return {
            tx,
            resolvedOperationalId: tx.operationalBill?.id
              ? String(tx.operationalBill.id)
              : undefined,
            resolvedTrustId: tx.trustRequestId,
          };
        }
      }

      // Try by operationalInvoiceId
      if ((data.operationalBillId || '').trim()) {
        const list = await juniperQueryApi.getTransactions({
          operationalInvoiceId: data.operationalBillId.trim(),
          limit: 1,
        });
        const tx = list.data[0];
        if (tx) {
          setLookupInfo({
            juniperId: tx.juniperId,
            operationalInvoiceId: String(tx.operationalBill?.id || ''),
            trustRequestId: tx.trustRequestId,
            clioPaymentLinkId: tx.clioPaymentLinkId,
            clioPaymentLinkUrl: tx.clioPaymentLinkUrl,
          });
          return {
            tx,
            resolvedOperationalId: String(tx.operationalBill?.id || ''),
            resolvedTrustId: tx.trustRequestId,
          };
        }
      }

      // Try by trustRequestId
      if ((data.trustRequestId || '').trim()) {
        const list = await juniperQueryApi.getTransactions({
          trustRequestId: data.trustRequestId.trim(),
          limit: 1,
        });
        const tx = list.data[0];
        if (tx) {
          setLookupInfo({
            juniperId: tx.juniperId,
            operationalInvoiceId: String(tx.operationalBill?.id || ''),
            trustRequestId: tx.trustRequestId,
            clioPaymentLinkId: tx.clioPaymentLinkId,
            clioPaymentLinkUrl: tx.clioPaymentLinkUrl,
          });
          return {
            tx,
            resolvedOperationalId: String(tx.operationalBill?.id || ''),
            resolvedTrustId: tx.trustRequestId,
          };
        }
      }

      setLookupInfo(null);
      return { tx: null };
    } catch (err) {
      // Non-fatal for test flow
      setLookupInfo(null);
      return { tx: null };
    }
  }

  const testDataMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!tenantId) {
        throw new Error('No tenant ID found. Please ensure you are logged in.');
      }

      // Try to resolve IDs from existing transaction if available
      const { resolvedOperationalId, resolvedTrustId } = await lookupExistingTransaction(data);
      const operationalBillIdToUse = (resolvedOperationalId || data.operationalBillId).trim();
      const trustRequestIdToUse = (resolvedTrustId || data.trustRequestId).trim();
      const hasOperationalId = operationalBillIdToUse !== '';

      if (!hasOperationalId) {
        throw new Error('Unable to resolve Operational Bill ID');
      }

      const request = {
        operationalBillId: parseInt(operationalBillIdToUse),
        ...(trustRequestIdToUse && { trrBillId: parseInt(trustRequestIdToUse) }),
        includePaymentLink: true, // Include payment link data and QR codes
      };

      return clioIntegrationApi.fetchJuniperTestData(request);
    },
    onSuccess: data => {
      setTestResults(data);
      setErrors({});
      // Save to session storage for use in Template Testing
      sessionStorage.setItem('juniperData', JSON.stringify(data));
      showToast.success('Juniper data fetched and saved to session successfully!');
    },
    onError: (error: Error) => {
      setErrors({ general: error.message });
      setTestResults(null);
      showToast.error(`Test failed: ${error.message}`);
    },
  });

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (errors[field] || errors.general) {
      setErrors(prev => ({ ...prev, [field]: undefined, general: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Reset previous errors
    setErrors({});

    // Validate form
    const newErrors: FormErrors = {};

    const hasOperationalId = formData.operationalBillId.trim() !== '';
    const hasTrustRequestId = formData.trustRequestId.trim() !== '';
    const hasJuniperId = (formData.juniperId || '').trim() !== '';

    // Require at least one of the identifiers
    if (!hasOperationalId && !hasTrustRequestId && !hasJuniperId) {
      newErrors.general =
        'Enter at least one: Juniper ID, Operational Bill ID, or Trust Request ID';
    }

    if (
      hasOperationalId &&
      (isNaN(Number(formData.operationalBillId)) || Number(formData.operationalBillId) <= 0)
    ) {
      newErrors.operationalBillId = 'Please enter a valid positive number';
    }

    if (
      hasTrustRequestId &&
      (isNaN(Number(formData.trustRequestId)) || Number(formData.trustRequestId) <= 0)
    ) {
      newErrors.trustRequestId = 'Please enter a valid positive number';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    testDataMutation.mutate(formData);
  };

  const downloadJson = () => {
    if (!testResults) return;

    const dataStr = JSON.stringify(testResults, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `juniper-data-${testResults.id}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast.success('JSON file downloaded successfully!');
  };

  const copyToClipboard = async () => {
    if (!testResults) return;

    try {
      const dataStr = JSON.stringify(testResults, null, 2);
      await navigator.clipboard.writeText(dataStr);
      showToast.success('JSON copied to clipboard!');
    } catch (error) {
      showToast.error('Failed to copy to clipboard');
    }
  };

  // Search functionality for JSON
  const searchInJson = useCallback((obj: any, term: string, path: string = ''): boolean => {
    if (!term) return true;

    const lowerTerm = term.toLowerCase();

    // Check if current path matches search
    if (path.toLowerCase().includes(lowerTerm)) return true;

    // Check if value matches search
    if (typeof obj === 'string' && obj.toLowerCase().includes(lowerTerm)) return true;
    if (typeof obj === 'number' && obj.toString().includes(term)) return true;

    // Recursively search object properties
    if (typeof obj === 'object' && obj !== null) {
      for (const [key, value] of Object.entries(obj)) {
        const newPath = path ? `${path}.${key}` : key;
        if (searchInJson(value, term, newPath)) return true;
      }
    }

    return false;
  }, []);

  // Filter JSON data based on search
  const filteredData =
    testResults && searchTerm
      ? Object.fromEntries(
          Object.entries(testResults).filter(([key, value]) => searchInJson(value, searchTerm, key))
        )
      : testResults;

  // Custom expand function that considers search
  const shouldExpandNode = useCallback(
    (_level: number, value: any, field?: string): boolean => {
      if (!searchTerm) return true; // Expand all nodes when not searching

      // Expand nodes that contain search matches
      if (typeof value === 'object' && value !== null) {
        return searchInJson(value, searchTerm, field || '');
      }

      return false;
    },
    [searchTerm, searchInJson]
  );

  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">Juniper Data</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Fetch Juniper data for testing templates. Provide any of: Juniper ID, Operational Bill ID,
          or Trust Request ID.
        </p>
      </CardHeader>
      <CardBody className="space-y-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Tenant Info */}
          <div className="bg-xtnd-light dark:bg-xtnd-dark/20 p-3 rounded-md">
            <p className="text-sm text-xtnd-dark dark:text-xtnd-white">
              <strong>Tenant ID:</strong> {tenantId || 'Not found'}
            </p>
            <p className="text-sm text-xtnd-dark dark:text-xtnd-white">
              <strong>User:</strong> {currentUser?.email || 'Not logged in'}
            </p>
          </div>

          {/* Input Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="juniperId">
                Juniper ID
                <span className="text-xs text-gray-500 ml-1">(Optional)</span>
              </Label>
              <Input
                id="juniperId"
                type="text"
                value={formData.juniperId}
                onChange={e => handleInputChange('juniperId' as any, e.target.value)}
                placeholder="e.g., JUN-abc123"
                className={errors.juniperId ? 'border-red-500' : ''}
                disabled={testDataMutation.isPending}
              />
              {errors.juniperId && <p className="text-sm text-red-600 mt-1">{errors.juniperId}</p>}
            </div>
            <div>
              <Label htmlFor="operationalBillId">
                Operational Bill ID
                <span className="text-xs text-gray-500 ml-1">(Optional)</span>
              </Label>
              <Input
                id="operationalBillId"
                type="number"
                value={formData.operationalBillId}
                onChange={e => handleInputChange('operationalBillId', e.target.value)}
                placeholder="e.g., 1170261462"
                className={errors.operationalBillId ? 'border-red-500' : ''}
                disabled={testDataMutation.isPending}
              />
              {errors.operationalBillId && (
                <p className="text-sm text-red-600 mt-1">{errors.operationalBillId}</p>
              )}
            </div>

            <div>
              <Label htmlFor="trustRequestId">
                Trust Request ID
                <span className="text-xs text-gray-500 ml-1">(Optional)</span>
              </Label>
              <Input
                id="trustRequestId"
                type="number"
                value={formData.trustRequestId}
                onChange={e => handleInputChange('trustRequestId', e.target.value)}
                placeholder="e.g., 1191860067"
                className={errors.trustRequestId ? 'border-red-500' : ''}
                disabled={testDataMutation.isPending}
              />
              {errors.trustRequestId && (
                <p className="text-sm text-red-600 mt-1">{errors.trustRequestId}</p>
              )}
            </div>
          </div>

          {/* General Error Message */}
          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
            </div>
          )}

          {/* Lookup Result Summary */}
          {lookupInfo && (
            <div className="bg-xtnd-light/60 dark:bg-xtnd-dark/30 border border-xtnd-light p-3 rounded-md text-sm">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <div>
                  <strong>Juniper ID:</strong> {lookupInfo.juniperId || '—'}
                </div>
                <div>
                  <strong>Operational ID:</strong> {lookupInfo.operationalInvoiceId || '—'}
                </div>
                <div>
                  <strong>Trust Request ID:</strong> {lookupInfo.trustRequestId || '—'}
                </div>
                <div>
                  <strong>Clio Link ID:</strong> {lookupInfo.clioPaymentLinkId || '—'}
                  {lookupInfo.clioPaymentLinkUrl && (
                    <a
                      href={lookupInfo.clioPaymentLinkUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 text-xtnd-blue underline"
                    >
                      Open
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            leftIcon={<Icon name="HiPlay" size="sm" />}
            loading={testDataMutation.isPending}
            disabled={!tenantId || testDataMutation.isPending}
            className="w-full"
          >
            {testDataMutation.isPending ? 'Fetching Data...' : 'Fetch Juniper Data'}
          </Button>
        </form>

        {/* Results Display */}
        {testResults && (
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
                Juniper Data Results
              </h3>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<Icon name="HiClipboard" size="sm" />}
                  onClick={copyToClipboard}
                >
                  Copy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<Icon name="HiDownload" size="sm" />}
                  onClick={downloadJson}
                >
                  Download JSON
                </Button>
              </div>
            </div>

            {/* Search Box */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="HiSearch" className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search JSON data... (e.g., 'bank_transactions', 'amount', 'client_name')"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Enhanced JSON Display with react-json-view-lite */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4 max-h-[600px] overflow-auto overflow-x-auto">
              <JsonView
                data={filteredData || {}}
                shouldExpandNode={shouldExpandNode}
                style={{
                  ...defaultStyles,
                  container: 'json-container font-mono text-sm',
                  label: 'text-xtnd-blue font-medium',
                  stringValue: 'text-green-600',
                  numberValue: 'text-blue-600',
                  booleanValue: 'text-purple-600',
                  nullValue: 'text-gray-500',
                  punctuation: 'text-gray-600',
                }}
                clickToExpandNode={true}
              />
            </div>

            {/* Path indicator when searching */}
            {searchTerm && (
              <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                <strong>Search active:</strong> Showing results for "{searchTerm}"
                {Object.keys(filteredData || {}).length === 0 && (
                  <span className="text-red-500 ml-2">No matches found</span>
                )}
              </div>
            )}

            {/* Summary Info */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 rounded-md">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div>
                  <strong>Juniper ID:</strong> {testResults.id}
                </div>
                <div>
                  <strong>Status:</strong> {testResults.status}
                </div>
                <div>
                  <strong>Created:</strong> {new Date(testResults.created_at).toLocaleDateString()}
                </div>
                <div>
                  <strong>Transactions:</strong> {testResults.trust?.bank_transactions?.length || 0}
                </div>
              </div>
              <div className="mt-2 text-xs text-green-700 dark:text-green-300">
                ✓ Data saved to session storage for use in Template Testing
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
